import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Send, MessageSquare, Bot, User, Database, Trash2, Settings, BarChart3, Paperclip, X } from "lucide-react";
import api from "../lib/api";
import LoadingSpinner from "../components/LoadingSpinner";
import MarkdownRenderer from "../components/MarkdownRenderer";
import ChartRenderer from "../components/ChartRenderer";
import { useAppSelector } from "../store/hooks";

interface ActionButton {
  label: string;
  value: string;
  type?: string;
}

interface AttachmentPayload {
  name: string;
  base64: string;
  type: string;
}

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: string;
  sources?: string[];
  isError?: boolean;
  canRetry?: boolean;
  classification?: string;
  confidence?: number;
  nodeUsed?: string;
  chartResponse?: any;
  actionButtons?: ActionButton[];
  attachments?: AttachmentPayload[];
  metadata?: {
    totalTime?: number;
    tokensUsed?: number;
    retrievalTime?: number;
    generationTime?: number;
    relevanceScore?: number;
    retrievedDocsCount?: number;
    [key: string]: any;
  };
}

interface LangGraphChatResponse {
  id?: string;
  query: string;
  response: string;
  userId: string;
  sessionId: string;
  projectId: string;
  classification?: string;
  confidence?: number;
  nodeUsed?: string;
  context?: any;
  actionButtons?: ActionButton[];
  metadata?: {
    model?: string;
    tokensUsed?: number;
    retrievalTime?: number;
    generationTime?: number;
    totalTime?: number;
    relevanceScore?: number;
    retrievedDocsCount?: number;
    organizationId?: string;
    [key: string]: any;
  };
  timestamp?: Date;
}

const ChatPage = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentUserMessage, setCurrentUserMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [session, setSession] = useState("");
  const [showCharts, setShowCharts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentPayload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentProject } = useAppSelector((state) => state.project);

  const generateSessionId = () =>
    `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error(`Could not read ${file.name}`));
          return;
        }
        const [, base64 = ""] = result.split(",");
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name}`));
      reader.readAsDataURL(file);
    });

  useEffect(() => {
    if (currentProject && !session) {
      const sessionKey = `langgraph_session_${currentProject.id}`;
      const storedSession = localStorage.getItem(sessionKey);
      if (storedSession) {
        setSession(storedSession);
      } else {
        const newSession = generateSessionId();
        setSession(newSession);
        localStorage.setItem(sessionKey, newSession);
      }
    }
  }, [currentProject, session]);

  useEffect(() => {
    if (session && currentProject) {
      const sessionKey = `langgraph_session_${currentProject.id}`;
      localStorage.setItem(sessionKey, session);
    }
  }, [session, currentProject]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSettings) {
        const target = event.target as Element;
        if (!target.closest(".settings-dropdown")) setShowSettings(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSettings]);

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      message,
      attachments,
    }: {
      message: string;
      attachments?: AttachmentPayload[];
    }) => {
      if (!currentProject) throw new Error("No project selected");
      const response = await api.post("/langgraph/chat", {
        query: message,
        projectId: currentProject.id,
        sessionId: session,
        attachments,
      });
      return response.data as LangGraphChatResponse;
    },
    onSuccess: (data) => {
      const sources =
        data.context?.sources ||
        data.context?.retrievedDocs?.map(
          (doc: any) => doc.fileName || doc.title
        ) ||
        [];
      const chartResponse = data.context?.chartResponse || null;

      const assistantMessage: Message = {
        id: Date.now().toString(),
        content: data.response,
        role: "assistant",
        timestamp: new Date().toISOString(),
        sources,
        classification: data.classification,
        confidence: data.confidence,
        nodeUsed: data.nodeUsed,
        chartResponse,
        actionButtons: data.actionButtons,
        metadata: data.metadata,
      };
      setSession(data.sessionId);
      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentUserMessage("");
      setPendingAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsProcessing(false);
    },
    onError: (error: any) => {
      const errorMessage: Message = {
        id: Date.now().toString(),
        content: `Error: ${error.message || "Failed to get response. Please try again."}`,
        role: "assistant",
        timestamp: new Date().toISOString(),
        sources: [],
        isError: true,
        canRetry: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsProcessing(false);
    },
  });

  const clearChatMutation = useMutation({
    mutationFn: async () => {
      if (!session) return;
      await api.delete(`/langgraph/memory/${session}`);
    },
    onSuccess: () => {
      setMessages([]);
      setCurrentUserMessage("");
      if (currentProject) {
        const sessionKey = `langgraph_session_${currentProject.id}`;
        localStorage.removeItem(sessionKey);
        const newSession = generateSessionId();
        setSession(newSession);
        localStorage.setItem(sessionKey, newSession);
      }
    },
    onError: (error: any) => console.error("Failed to clear chat:", error),
  });

  const handleClearChat = () => {
    if (
      window.confirm(
        "Are you sure you want to clear the chat history? This action cannot be undone."
      )
    ) {
      clearChatMutation.mutate();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const clearPendingAttachments = () => {
    setPendingAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const buildAttachmentBubbleText = (text: string, attachments: AttachmentPayload[]) => {
    if (text.trim()) return text;
    if (attachments.length === 1) {
      return `Uploaded attachment: ${attachments[0].name}`;
    }
    return `Uploaded ${attachments.length} attachments`;
  };

  /** Core send — used both by the form and by action-button clicks */
  const sendMessage = (
    text: string,
    attachments: AttachmentPayload[] = pendingAttachments
  ) => {
    const trimmedText = text.trim();
    const hasAttachments = attachments.length > 0;
    if ((!trimmedText && !hasAttachments) || isProcessing || !currentProject) return;

    const requestMessage = trimmedText || "ADD_ATTACHMENT";
    const userMessage: Message = {
      id: Date.now().toString(),
      content: buildAttachmentBubbleText(trimmedText, attachments),
      role: "user",
      timestamp: new Date().toISOString(),
      attachments: hasAttachments ? attachments : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentUserMessage(requestMessage);
    setIsProcessing(true);
    sendMessageMutation.mutate({
      message: requestMessage,
      attachments: hasAttachments ? attachments : undefined,
    });
    setInputMessage("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  const handleActionButton = (btn: ActionButton) => {
    if (!btn.value.trim() || isProcessing || !currentProject) return;
    // Show the label in the chat bubble, but send the value to the API
    const attachments = pendingAttachments;
    const hasAttachments = attachments.length > 0;
    const userMessage: Message = {
      id: Date.now().toString(),
      content: btn.label,
      role: "user",
      timestamp: new Date().toISOString(),
      attachments: hasAttachments ? attachments : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentUserMessage(btn.value);
    setIsProcessing(true);
    sendMessageMutation.mutate({
      message: btn.value,
      attachments: hasAttachments ? attachments : undefined,
    });
  };

  const handleRetry = () => {
    if (isProcessing || !currentProject || !currentUserMessage) return;
    setMessages((prev) => prev.slice(0, -1));
    setIsProcessing(true);
    sendMessageMutation.mutate({ message: currentUserMessage });
  };

  const handleAttachmentSelection = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          type: file.type || "application/octet-stream",
          base64: await fileToBase64(file),
        }))
      );
      setPendingAttachments((prev) => [...prev, ...attachments]);
    } catch (error) {
      console.error("Failed to prepare attachments", error);
      window.alert("Failed to read one or more attachments. Please try again.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removePendingAttachment = (name: string) => {
    setPendingAttachments((prev) => prev.filter((attachment) => attachment.name !== name));
  };

  const formatTime = (timestamp: string) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!currentProject ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-8 text-center text-gray-500">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Project Selected
            </h3>
            <p>
              Please select a project from the header to start chatting with
              your documents.
            </p>
          </div>
        </div>
      ) : (
        <div
          className="bg-white shadow rounded-lg flex flex-col"
          style={{ height: "calc(100vh - 200px)" }}
        >
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Chat</h1>
                <p className="text-gray-600">
                  Chat with your AI assistant for{" "}
                  <span className="font-medium">{currentProject.name}</span>
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Database className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {currentProject.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    {Math.floor(messages.length / 2)} conversations
                  </span>
                </div>

                {/* Settings */}
                <div className="relative settings-dropdown">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    title="Chat Settings"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Settings
                  </button>
                  {showSettings && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                      <div className="p-4 space-y-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          Chat Settings
                        </h3>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <BarChart3 className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">
                              Show Charts
                            </span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={showCharts}
                              onChange={(e) => setShowCharts(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        <div className="text-xs text-gray-500">
                          {showCharts
                            ? "Charts will be displayed when available"
                            : "Charts are hidden in responses"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {messages.length > 0 && (
                  <button
                    onClick={handleClearChat}
                    disabled={clearChatMutation.isPending}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {clearChatMutation.isPending ? (
                      <>
                        <LoadingSpinner size="small" />
                        <span className="ml-1">Clearing...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Clear Chat
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Start a conversation
                  </h3>
                  <p className="text-gray-500">
                    Ask questions, get insights, and interact with your AI
                    assistant for {currentProject.name}
                  </p>
                </div>
              ) : (
                messages.map((message, msgIndex) => {
                  // Buttons are only active on the very last assistant message
                  const isLastAssistant =
                    message.role === "assistant" &&
                    msgIndex === messages.length - 1 &&
                    !isProcessing;

                  return (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.role === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-3xl rounded-lg p-4 ${
                          message.role === "user"
                            ? "bg-[#13afe3] text-white"
                            : message.isError
                            ? "bg-red-50 border border-red-200"
                            : "bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            {message.role === "user" ? (
                              <User className="w-5 h-5" />
                            ) : (
                              <Bot className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            {/* Message body */}
                            {message.role === "assistant" &&
                            !message.isError ? (
                              <MarkdownRenderer
                                content={message.content}
                                className="text-sm leading-relaxed"
                              />
                            ) : (
                              <p
                                className={`text-sm leading-relaxed ${
                                  message.isError ? "text-red-800" : ""
                                }`}
                              >
                                {message.content}
                              </p>
                            )}

                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {message.attachments.map((attachment) => (
                                  <span
                                    key={`${message.id}-${attachment.name}`}
                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                                      message.role === "user"
                                        ? "bg-white/20 text-white"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    <Paperclip className="mr-1 h-3 w-3" />
                                    {attachment.name}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* ── ACTION BUTTONS ── */}
                            {message.role === "assistant" &&
                              message.actionButtons &&
                              message.actionButtons.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {message.actionButtons.map((btn, i) => {
                                    const isRecommended =
                                      btn.type === "SUGGESTION_RECOMMENDED";
                                    const isDisabled =
                                      !isLastAssistant || isProcessing;
                                    return (
                                      <button
                                        key={i}
                                        onClick={() =>
                                          isLastAssistant &&
                                          handleActionButton(btn)
                                        }
                                        disabled={isDisabled}
                                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all whitespace-pre-line text-left ${
                                          isDisabled
                                            ? "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed opacity-50"
                                            : isRecommended
                                            ? "border-[#13afe3] bg-[#13afe3] text-white hover:bg-[#0e9bc7] cursor-pointer shadow-sm"
                                            : "border-[#13afe3] text-[#13afe3] bg-white hover:bg-[#13afe3] hover:text-white cursor-pointer shadow-sm"
                                        }`}
                                      >
                                        {isRecommended
                                          ? `✓ ${btn.label}  ·  Suggested`
                                          : btn.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                            {/* Chart rendering */}
                            {message.role === "assistant" &&
                              message.chartResponse &&
                              showCharts && (
                                <ChartRenderer
                                  chartData={message.chartResponse}
                                />
                              )}

                            {/* Classification / confidence */}
                            {message.role === "assistant" &&
                              (message.classification ||
                                message.confidence) && (
                                <div className="mt-3 pt-3 border-t border-gray-300">
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    {message.classification && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Type:
                                        </span>
                                        <span
                                          className={`px-2 py-1 rounded text-xs ${
                                            message.classification === "RAG"
                                              ? "bg-blue-100 text-blue-800"
                                              : message.classification ===
                                                "EXECUTE_QUERY"
                                              ? "bg-green-100 text-green-800"
                                              : "bg-gray-100 text-gray-800"
                                          }`}
                                        >
                                          {message.classification}
                                        </span>
                                      </div>
                                    )}
                                    {message.confidence && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Confidence:
                                        </span>
                                        <span
                                          className={`px-2 py-1 rounded text-xs ${
                                            message.confidence > 0.8
                                              ? "bg-green-100 text-green-800"
                                              : message.confidence > 0.5
                                              ? "bg-yellow-100 text-yellow-800"
                                              : "bg-red-100 text-red-800"
                                          }`}
                                        >
                                          {(
                                            message.confidence * 100
                                          ).toFixed(1)}
                                          %
                                        </span>
                                      </div>
                                    )}
                                    {message.nodeUsed && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Node:
                                        </span>
                                        <span className="text-gray-600">
                                          {message.nodeUsed}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Sources */}
                            {message.sources && message.sources.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-300">
                                <p className="text-xs text-gray-500 mb-2">
                                  Sources:
                                </p>
                                <div className="space-y-1">
                                  {message.sources.map((source, index) => (
                                    <span
                                      key={index}
                                      className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded mr-2"
                                    >
                                      {source}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Metadata */}
                            {message.role === "assistant" &&
                              message.metadata && (
                                <div className="mt-3 pt-3 border-t border-gray-300">
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    {message.metadata.totalTime && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Response Time:
                                        </span>
                                        <span>
                                          {message.metadata.totalTime}ms
                                        </span>
                                      </div>
                                    )}
                                    {message.metadata.tokensUsed && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Tokens:
                                        </span>
                                        <span>
                                          {message.metadata.tokensUsed}
                                        </span>
                                      </div>
                                    )}
                                    {message.metadata.retrievedDocsCount && (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-medium">
                                          Docs:
                                        </span>
                                        <span>
                                          {message.metadata.retrievedDocsCount}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Retry */}
                            {message.isError && message.canRetry && (
                              <div className="mt-3 pt-3 border-t border-red-200">
                                <button
                                  onClick={handleRetry}
                                  disabled={isProcessing}
                                  className="inline-flex items-center px-3 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isProcessing ? (
                                    <>
                                      <LoadingSpinner size="small" />
                                      <span className="ml-1">Retrying...</span>
                                    </>
                                  ) : (
                                    "Retry"
                                  )}
                                </button>
                              </div>
                            )}

                            <p
                              className={`text-xs mt-2 ${
                                message.role === "user"
                                  ? "text-indigo-200"
                                  : message.isError
                                  ? "text-red-600"
                                  : "text-gray-500"
                              }`}
                            >
                              {formatTime(message.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Bot className="w-5 h-5 text-gray-600" />
                      <div className="flex items-center space-x-2">
                        <LoadingSpinner size="small" />
                        <span className="text-sm text-gray-500">
                          Thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 px-6 py-4">
            {pendingAttachments.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {pendingAttachments.map((attachment) => (
                  <span
                    key={attachment.name}
                    className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800"
                  >
                    <Paperclip className="mr-1 h-3 w-3" />
                    {attachment.name}
                    <button
                      type="button"
                      onClick={() => removePendingAttachment(attachment.name)}
                      className="ml-2 text-blue-700 hover:text-blue-900"
                      disabled={isProcessing}
                      aria-label={`Remove ${attachment.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearPendingAttachments}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  disabled={isProcessing}
                >
                  Clear all
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="flex space-x-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleAttachmentSelection}
                  className="hidden"
                  disabled={isProcessing}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Add attachments"
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={`Ask your AI assistant about ${currentProject.name}...`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  disabled={isProcessing}
                />
                <button
                  type="submit"
                  disabled={(!inputMessage.trim() && pendingAttachments.length === 0) || isProcessing}
                  className="px-6 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-color, #13afe3)" }}
                >
                  {isProcessing ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
